import { Injectable } from "@angular/core";
import { AppConstants } from "@core/constants";
import { ApiBaseService } from "@core/services/api-base.service";
import { IMaximumSpeedPowerSettings, IMaximumSpeedPowerSettingsWrapper } from "@shared/interfaces/metric-settings.interface";
import { BehaviorSubject, delay, distinctUntilChanged, finalize } from "rxjs";

export interface StateStatus {
  status: 'ok' | 'warning' | 'error';
  message: string
}

export type ApiStatus = 'loading' | 'saving' | null;

@Injectable()
export class MaximumSpeedPowerComponentState {
  private readonly _apiStatus = new BehaviorSubject<ApiStatus>(null);
  private readonly _settings = new BehaviorSubject<IMaximumSpeedPowerSettings | null>(null);
  private readonly _status = new BehaviorSubject<StateStatus | null>(null);

  public apiStatus$ = this._apiStatus.asObservable().pipe(delay(0), distinctUntilChanged());
  public settings$ = this._settings.asObservable();
  public status$ = this._status.asObservable().pipe(delay(0), distinctUntilChanged());

  constructor(
    private _apiService: ApiBaseService
  ) { }

  public loadSettings(featureId: number): void {
    this._apiStatus.next('loading');

    const url = `${AppConstants.API.FEATURE_ITEM.GET}${featureId}/settings/`;
    this._apiService.getJson<IMaximumSpeedPowerSettingsWrapper>(url)
      .pipe(
        finalize(() => {
          this._apiStatus.next(null);
        })
      ).subscribe({
        next: (settings: IMaximumSpeedPowerSettingsWrapper) => {
          this._settings.next(settings.settings);
        },
        error: (err) => {
          this._status.next({
            status: 'warning',
            message: 'Unable to load settings for Maximum Speed/Power. Showing default values instead'
          });
          console.error(err);
        }
      })
  }

  public saveSettings(featureId: number, settings: Record<string, any>): void {
    this._apiStatus.next('saving');

    const url = `${AppConstants.API.FEATURE_ITEM.GET}${featureId}/settings/`;
    this._apiService.postJson<IMaximumSpeedPowerSettingsWrapper>(url, { settings })
      .pipe(
        finalize(() => {
          this._apiStatus.next(null);
        })
      ).subscribe({
        next: (_response: IMaximumSpeedPowerSettingsWrapper) => {
          this._status.next({
            status: 'ok',
            message: 'Successfully saved settings for Maximum Speed/Power'
          });
        },
        error: (err) => {
          this._status.next({
            status: 'error',
            message: 'Unable to save settings for Maximum Speed/Power'
          });
          console.error(err);
        }
      })
  }
}